/**
 * VIB-642 PR 6.4: Cron heartbeat-tabell på admin/observability.
 *
 * Viser per forventet PO-sync (definert i useCronHeartbeats):
 *  - jobname + label
 *  - siste run (når) + varighet
 *  - status-badge (ok/stale/error/missing)
 *  - antall runs siste 24h + errors
 *
 * Komponenten holder kun presentasjonslogikk; all datahenting går via
 * useCronHeartbeats-hooken.
 */
import { useCronHeartbeats, type CronHeartbeat, type CronJobStatus } from "@/hooks/use-cron-heartbeats";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatDateTimeShort } from "@/lib/format";

const STATUS_LABEL: Record<CronJobStatus, string> = {
  ok: "OK",
  error: "Feilet",
  stale: "Stale",
  missing: "Mangler",
};

const STATUS_CLASS: Record<CronJobStatus, string> = {
  ok: "bg-emerald-100 text-emerald-800 border-emerald-200",
  error: "bg-rose-100 text-rose-800 border-rose-200",
  stale: "bg-amber-100 text-amber-800 border-amber-200",
  missing: "bg-muted text-muted-foreground border-transparent",
};

function StatusBadge({ status }: { status: CronJobStatus }) {
  return (
    <Badge variant="outline" className={STATUS_CLASS[status]}>
      {STATUS_LABEL[status]}
    </Badge>
  );
}

function formatDuration(ms: number | null): string {
  if (ms == null) return "—";
  if (ms < 1000) return `${ms} ms`;
  return `${(ms / 1000).toFixed(1)} s`;
}

function Row({ row }: { row: CronHeartbeat }) {
  return (
    <TableRow>
      <TableCell className="font-medium">
        <div>{row.label}</div>
        <div className="text-xs text-muted-foreground font-mono">
          {row.jobname}
        </div>
      </TableCell>
      <TableCell>
        <StatusBadge status={row.lastStatus} />
      </TableCell>
      <TableCell>{formatDateTimeShort(row.lastRun)}</TableCell>
      <TableCell>{formatDuration(row.durationMs)}</TableCell>
      <TableCell className="text-right">{row.runsLast24h}</TableCell>
      <TableCell className="text-right">
        {row.errorsLast24h > 0 ? (
          <span className="text-rose-700 font-semibold">{row.errorsLast24h}</span>
        ) : (
          <span className="text-muted-foreground">0</span>
        )}
      </TableCell>
    </TableRow>
  );
}

export function CronHeartbeatsCard() {
  const { data, isLoading, isError, error } = useCronHeartbeats();

  return (
    <Card>
      <CardHeader>
        <CardTitle>Cron-jobber</CardTitle>
        <p className="text-sm text-muted-foreground">
          Heartbeats fra <code className="font-mono text-xs">cron_run_log</code>.
          Stale = ikke fyrt på lenger enn forventet intervall.
        </p>
      </CardHeader>
      <CardContent>
        {isLoading && (
          <div className="text-sm text-muted-foreground py-4">Laster heartbeats...</div>
        )}
        {isError && (
          <div className="text-sm text-rose-700 py-4">
            Kunne ikke laste heartbeats: {(error as Error).message}
          </div>
        )}
        {!isLoading && !isError && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Jobb</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Sist kjørt</TableHead>
                <TableHead>Varighet</TableHead>
                <TableHead className="text-right">Runs 24h</TableHead>
                <TableHead className="text-right">Errors 24h</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(data ?? []).map((row) => (
                <Row key={row.jobname} row={row} />
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
